import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, CheckCircle2, XCircle, Clock, Play, AlertTriangle, TrendingUp, Server } from 'lucide-react';
import { pipelinesApi, buildsApi } from '../api/client';
import { formatDistanceToNow } from 'date-fns';
import { clsx } from 'clsx';

const MOCK_BUILDS = [
  { id: 'b1', service: 'cloud-server', status: 'success', branch: 'main', commit: 'a3f2b1c', duration: 142, startTime: new Date(Date.now() - 900000).toISOString(), env: 'production' },
  { id: 'b2', service: 'cloud-frontend', status: 'running', branch: 'feature/auth', commit: 'f9e8d7c', duration: null, startTime: new Date(Date.now() - 180350).toISOString(), env: 'staging' },
  { id: 'b3', service: 'cloud-admin', status: 'failed', branch: 'fix/login', commit: 'c4d5e6f', duration: 67, startTime: new Date(Date.now() - 3600000).toISOString(), env: 'dev' },
  { id: 'b4', service: 'cloud-server', status: 'success', branch: 'main', commit: 'b2c3d4e', duration: 138, startTime: new Date(Date.now() - 7200000).toISOString(), env: 'staging' },
];

const MOCK_SERVICES = [
  { name: 'cloud-server', dev: 'healthy', staging: 'healthy', production: 'healthy' },
  { name: 'cloud-admin', dev: 'healthy', staging: 'degraded', production: 'healthy' },
  { name: 'cloud-frontend', dev: 'healthy', staging: 'healthy', production: 'healthy' },
  { name: 'cloud-api', dev: 'degraded', staging: 'healthy', production: 'healthy' },
  { name: 'cloud-worker', dev: 'healthy', staging: 'idle', production: 'idle' },
];

const ENV_CONFIG = {
  dev: { label: 'Development', color: '#3b82f6', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  staging: { label: 'Staging', color: '#f59e0b', bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
  production: { label: 'Production', color: '#22c55e', bg: 'bg-green-500/10', text: 'text-green-400' },
};

function StatusIcon({ status, size = 14 }) {
  if (status === 'success' || status === 'healthy') return <CheckCircle2 size={size} className="text-green-400" />;
  if (status === 'running') return <div className="w-3.5 h-3.5 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />;
  if (status === 'failed' || status === 'degraded') return <XCircle size={size} className="text-red-400" />;
  return <Clock size={size} className="text-slate-500" />;
}

function BuildRow({ build }) {
  const env = ENV_CONFIG[build.env];
  return (
    <Link to={`/builds/${build.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group border-b border-slate-100 last:border-0">
      <StatusIcon status={build.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{build.service}</span>
          <span className={`badge ${env.bg} ${env.text}`}>{env.label}</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5 truncate">
          {build.branch} · <span className="font-mono">{build.commit}</span>
        </p>
      </div>
      <div className="text-right shrink-0">
        {build.duration && <p className="text-xs text-slate-400">{build.duration}s</p>}
        <p className="text-xs text-slate-500">{formatDistanceToNow(new Date(build.startTime), { addSuffix: true })}</p>
      </div>
    </Link>
  );
}

function ServiceGrid() {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server size={14} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-900">Services</span>
        </div>
        <Link to="/services" className="text-xs text-accent-purple hover:text-accent-purple-light transition-colors">View all</Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="px-4 py-2.5 text-left text-xs text-slate-500 font-medium">Service</th>
              {['dev', 'staging', 'production'].map(env => (
                <th key={env} className="px-4 py-2.5 text-center text-xs text-slate-500 font-medium capitalize">{env}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MOCK_SERVICES.map(svc => (
              <tr key={svc.name} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 text-sm font-mono text-slate-700">{svc.name}</td>
                {['dev', 'staging', 'production'].map(env => (
                  <td key={env} className="px-4 py-3 text-center">
                    <StatusIcon status={svc[env]} size={12} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pipelinesApi.list()
      .then(setPipelines)
      .catch(() => setPipelines([]))
      .finally(() => setLoading(false));
  }, []);

  const successCount = MOCK_BUILDS.filter(b => b.status === 'success').length;
  const failedCount = MOCK_BUILDS.filter(b => b.status === 'failed').length;
  const runningCount = MOCK_BUILDS.filter(b => b.status === 'running').length;

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Builds', value: runningCount, icon: Activity, color: 'text-purple-400', bg: 'bg-purple-500/10' },
          { label: 'Successful Today', value: successCount, icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Failed Today', value: failedCount, icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Pipelines', value: loading ? '…' : pipelines.length, icon: TrendingUp, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card px-4 py-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center`}>
                <Icon size={18} className={stat.color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Environments */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(ENV_CONFIG).map(([id, env]) => {
          const healthy = MOCK_SERVICES.filter(s => s[id] === 'healthy').length;
          return (
            <div key={id} className="card p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: env.color }} />
                <span className="text-sm font-semibold text-slate-900">{env.label}</span>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{healthy}/{MOCK_SERVICES.length}</p>
                  <p className="text-xs text-slate-500">services healthy</p>
                </div>
                <Link to="/triggers" className="btn-primary text-xs py-1.5 px-3">
                  <Play size={11} /> Triggers
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Builds */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-slate-400" />
              <span className="text-sm font-semibold text-slate-900">Recent Builds</span>
            </div>
            <Link to="/builds" className="text-xs text-accent-purple hover:text-accent-purple-light transition-colors">View all</Link>
          </div>
          <div>
            {MOCK_BUILDS.map(b => <BuildRow key={b.id} build={b} />)}
          </div>
        </div>

        {/* Service Grid */}
        <ServiceGrid />
      </div>
    </div>
  );
}
